import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  User,
  Eye
} from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui';

interface Bid {
  id?: string;
  _id?: string;
  buyer: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  bidPrice: number;
  status: 'open' | 'countered' | 'ACCEPTED' | 'REJECTED' | 'expired' | 'EXPIRED' | 'CANCELLED' | 'PENDING' | 'CONVERTED';
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  adminOverride?: boolean;
  adminReason?: string;
}

interface ProductDetails {
  id: string;
  name: string;
  price: number;
  images: string[];
  description?: string;
  seller: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ProductBidsResponse {
  product: ProductDetails;
  bids: Bid[];
  total: number;
  statistics: {
    totalBids: number;
    pendingBids: number;
    acceptedBids: number;
    rejectedBids: number;
    highestBid: number;
    lowestBid: number;
    avgBidPrice: number;
  };
}

const ProductBids: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const { t } = useTranslation('bidding');
  const navigate = useNavigate();
  const [data, setData] = useState<ProductBidsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchProductBids = useCallback(async () => {
    if (!productId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/admin/products/${productId}/offers`);

      const raw = response.data.data;
      const offers = raw.offers || raw.bids || [];
      const normalizedData = {
        ...raw,
        bids: offers.map((bid: Bid & { amount?: number }) => ({
          ...bid,
          id: bid.id || bid._id?.toString() || bid._id,
          bidPrice: bid.bidPrice ?? bid.amount ?? 0,
        })),
        statistics: raw.statistics
          ? {
              totalBids: raw.statistics.totalOffers ?? raw.statistics.totalBids,
              pendingBids: raw.statistics.pendingOffers ?? raw.statistics.pendingBids,
              acceptedBids: raw.statistics.acceptedOffers ?? raw.statistics.acceptedBids,
              rejectedBids: raw.statistics.rejectedOffers ?? raw.statistics.rejectedBids,
              highestBid: raw.statistics.highestOffer ?? raw.statistics.highestBid,
              lowestBid: raw.statistics.lowestOffer ?? raw.statistics.lowestBid,
              avgBidPrice: raw.statistics.avgOfferAmount ?? raw.statistics.avgBidPrice,
            }
          : raw.statistics,
      };
      
      setData(normalizedData);
    } catch (error: any) {
      console.error('Error fetching product bids:', error);
      // Set null data on error to show error state
      setData(null);
      if (error.response?.status === 401) {
        console.error('Unauthorized - please log in again');
      }
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    if (productId) {
      fetchProductBids();
    }
  }, [productId, fetchProductBids]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'countered': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ACCEPTED': return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECTED': return 'bg-red-100 text-red-800 border-red-200';
      case 'CANCELLED': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'expired':
      case 'EXPIRED': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'CONVERTED': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const statusLabel = (status: string) =>
    t(`status.${status}`, { defaultValue: status });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const truncateText = (text: string, maxLength: number = 20) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('productPage.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">{t('productPage.notFound')}</h2>
          <Button
            onClick={() => navigate('/offers')}
            className="mt-4"
          >
            {t('actions.backToOffers')}
          </Button>
        </div>
      </div>
    );
  }

  const { product, bids, statistics } = data;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/offers')}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t('actions.backToOffers')}</span>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('productPage.title')}</h1>
            <p className="text-gray-600 mt-1">{t('productPage.subtitle', { name: product.name })}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Overview */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('overview.productInfo')}</h2>
            
            <div className="flex items-start space-x-6">
              <img
                src={product.images[0] || '/placeholder-product.png'}
                alt={product.name}
                className="w-24 h-24 rounded-lg object-cover"
              />
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">{product.name}</h3>
                <p className="text-sm text-gray-500 mb-2">{t('overview.originalPrice')}: {formatCurrency(product.price)}</p>
                <div className="text-sm text-gray-600">
                  <p><strong>Seller:</strong> {product.seller.firstName} {product.seller.lastName}</p>
                  <p><strong>Email:</strong> {product.seller.email}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Bids Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {t('productPage.allOffers', { count: bids.length })}
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <colgroup>
                  <col className="w-[30%]" />
                  <col className="w-[20%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('fields.buyer')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('fields.offerAmount')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('fields.status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('fields.createdAt')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bids.map((bid, index) => (
                    <tr key={bid.id || bid._id || `bid-${index}`} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="ml-3 min-w-0 flex-1">
                            <div 
                              className="text-sm font-medium text-gray-900 truncate"
                              title={`${bid.buyer.firstName} ${bid.buyer.lastName}`}
                            >
                              {truncateText(`${bid.buyer.firstName} ${bid.buyer.lastName}`, 18)}
                            </div>
                            <div 
                              className="text-xs text-gray-500 truncate"
                              title={bid.buyer.email}
                            >
                              {truncateText(bid.buyer.email, 20)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-bold text-green-600">
                          {formatCurrency(bid.bidPrice)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {((bid.bidPrice / product.price) * 100).toFixed(1)}%
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(bid.status)}`}>
                          {statusLabel(bid.status)}
                        </span>
                        {bid.adminOverride && (
                          <div className="text-xs text-orange-600 mt-1">Override</div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-500">
                        {new Date(bid.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-center space-x-1">
                          <Link
                            to={`/offers/${bid.id || bid._id}`}
                            className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-full"
                            title={t('actions.viewDetails')}
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Statistics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('statistics.title')}</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('statistics.totalBids')}:</span>
                <span className="text-sm font-medium">{statistics.totalBids}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('statistics.pendingBids')}:</span>
                <span className="text-sm font-medium text-yellow-600">{statistics.pendingBids}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('statistics.acceptedBids')}:</span>
                <span className="text-sm font-medium text-green-600">{statistics.acceptedBids}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('statistics.rejectedBids')}:</span>
                <span className="text-sm font-medium text-red-600">{statistics.rejectedBids}</span>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">{t('productPage.highestOffer')}:</span>
                  <span className="text-sm font-semibold text-green-600">
                    {formatCurrency(statistics.highestBid)}
                  </span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">{t('productPage.lowestOffer')}:</span>
                  <span className="text-sm font-medium">
                    {formatCurrency(statistics.lowestBid)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('productPage.averageOffer')}:</span>
                  <span className="text-sm font-medium">
                    {formatCurrency(statistics.avgBidPrice)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ProductBids;